import type { HomepagePreview } from '@/api/repository/types/types';
import {
    addPhoto,
    applyAutoMain,
    applyFallbackMain,
    canAddPhoto,
    canApplyAutoMain,
    ClinicPhotoState,
    editPhotoStateOf,
    homepageKey,
    initialHomepageKey,
    isStaleHomepageResult,
    MAX_LIST_IMAGES,
    photoStateOf,
    realPhotoCount,
    restoreOriginalMain,
    shouldReadHomepage,
} from './homepagePhotoRules';

/**
 * 홈페이지 대표 사진 자동 등록 엔진 — React 밖에서 상태·비동기 흐름을 갖는다(useHomepagePhotoAuto 가 감싼다, 검사는 __checks__).
 * - 홈페이지를 읽어 사진 후보를 받고, 대표를 바꿔도 되는 상태(비었음·기본 이미지·자동으로 넣은 사진·수정 화면을 열 때 저장돼 있던 사진)면
 *   첫 후보를 서버에 저장해 대표로 넣는다(저장돼 있던 대표는 추가 사진으로 남긴다 — 5장 꽉 차면 그대로).
 * - 같은 주소는 다시 읽지 않고(수정 화면은 저장된 주소에서 시작), 늦게 온 결과(주소 바뀜·새 읽기 시작)는 버린다.
 * - 수정 화면에서 주소를 저장된 원래 주소로 되돌리면 읽지 않고 처음 사진 상태로 되돌린다.
 * - 실패(예외·결과 없음·failure·첫 사진 저장 실패)면 열쇠를 지워 다음에 칸에서 벗어날 때 다시 읽는다.
 *   가입은 기본 이미지(자동 사진은 빼고), 수정은 자동으로 넣었던 대표를 저장된 원래 사진으로.
 * - 나머지 후보는 '더 넣기' 줄 없이 치과 이미지 목록 뒤에 바로 넣는다(5장을 넘어도 모두 — 저장할 때 5장으로 고른다, 2026-10-30). 이렇게 자동으로 넣은 추가 사진은
 *   주소를 바꾸거나·비우거나·원래 주소로 되돌리거나·실패하면 빠진다(직접 올린 사진·저장돼 있던 사진은 그대로).
 * - 읽기 + 사진 저장 전체가 [timeoutMs] 를 넘으면 실패로 보고 풀린다(화면이 멈추지 않게).
 */
export type HomepagePhotoMode = 'signup' | 'edit';

export interface HomepagePhotoEngineOptions {
    mode: HomepagePhotoMode;
    /** 시작 치과 이미지(수정 화면 — 저장된 이미지) */
    initialImages?: string[];
    /** 수정 화면 — 저장된 홈페이지 주소(이 주소 그대로면 칸에서 벗어나도 읽지 않는다) */
    initialUrl?: string | null;
    /** 지금 홈페이지 칸 값 — 늦게 온 결과를 가를 때 쓴다 */
    currentUrl: () => string;
    readPreview: (url: string) => Promise<{ result?: HomepagePreview; message?: string }>;
    importPhoto: (candidateUrl: string) => Promise<{ result?: { url: string }; message?: string }>;
    /** 읽기 + 첫 사진 저장을 합쳐 기다리는 시간 */
    timeoutMs?: number;
}

/** 사진 칸 안내 — info(일반 안내: 5장 등)·error(빨강: 저장 실패) */
export interface PhotoNotice {
    text: string;
    tone: 'info' | 'error';
}

export interface HomepagePhotoSnapshot {
    state: ClinicPhotoState;
    loading: boolean;
    preview: HomepagePreview | null;
    /** 홈페이지 칸 아래 안내 */
    message: string;
    /** 사진 칸 안 한 줄 — 수정 화면에서 자동으로 대표를 바꿨을 때 */
    fieldMessage: string;
    photoNotice: PhotoNotice | null;
    /** 지금 저장 중인 후보(원래 주소) */
    pickingCandidate: string | null;
    /** 후보 원래 주소 → 서버에 저장한 우리 주소 */
    savedByCandidate: Record<string, string>;
}

export const HOMEPAGE_PHOTO_TIMEOUT_MS = 25_000;
export const HOMEPAGE_PHOTO_FULL_MESSAGE = '치과 이미지가 너무 많아요. 한 장을 빼고 넣어 주세요.';
/** 읽는 중 [회원가입]·[저장]을 누르면 */
export const HOMEPAGE_PHOTO_BUSY_MESSAGE = '홈페이지 사진을 찾는 중이에요. 잠시만 기다려 주세요.';

class HomepagePhotoTimeout extends Error {}

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new HomepagePhotoTimeout('시간 초과')), Math.max(0, ms));
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (error) => { clearTimeout(timer); reject(error); },
        );
    });

/** 서버 오류 문구 — 없으면 [fallback] */
const messageOf = (error: unknown, fallback: string): string => {
    if (error instanceof HomepagePhotoTimeout) return fallback;
    const message: unknown = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
    return error instanceof Error && error.message && !/^Request failed/i.test(error.message) ? error.message : fallback;
};

export class HomepagePhotoEngine {
    private snap: HomepagePhotoSnapshot;
    private readonly original: ClinicPhotoState;
    /** 수정 화면 — 저장된 홈페이지 주소 열쇠(가입은 null) */
    private readonly initialKey: string | null;
    private lastKey: string | null;
    private seq = 0;
    /** 홈페이지 결과로 자동으로 넣은 추가 사진(우리 주소) — 주소가 바뀌면 뺀다 */
    private autoExtras: Set<string> = new Set();
    private listener: (snapshot: HomepagePhotoSnapshot) => void = () => undefined;

    constructor(private readonly options: HomepagePhotoEngineOptions) {
        // 수정 화면은 가입처럼 0장이면 기본 이미지, 저장돼 있던 사진은 saved(새 홈페이지 사진이 대표를 넘겨받는다)
        this.original = options.mode === 'edit' ? editPhotoStateOf(options.initialImages) : photoStateOf(options.initialImages);
        this.initialKey = options.mode === 'edit' ? initialHomepageKey(options.initialUrl) : null;
        this.lastKey = this.initialKey;
        this.snap = {
            state: this.original,
            loading: false,
            preview: null,
            message: '',
            fieldMessage: '',
            photoNotice: null,
            pickingCandidate: null,
            savedByCandidate: {},
        };
    }

    get snapshot(): HomepagePhotoSnapshot {
        return this.snap;
    }

    /** 읽기·사진 저장 중 — [회원가입]·[저장]을 막는다(시간 초과면 풀린다) */
    get busy(): boolean {
        return this.snap.loading || !!this.snap.pickingCandidate;
    }

    get mode(): HomepagePhotoMode {
        return this.options.mode;
    }

    subscribe(listener: (snapshot: HomepagePhotoSnapshot) => void): void {
        this.listener = listener;
    }

    /** 화면이 사라졌다 — 늦게 온 결과는 버리고, 다시 붙으면(개발 모드 두 번 마운트) 처음 열쇠부터 */
    reset(): void {
        this.seq += 1;
        this.lastKey = this.initialKey;
        if (this.snap.loading) this.set({ loading: false });
    }

    private get timeoutMs(): number {
        return this.options.timeoutMs ?? HOMEPAGE_PHOTO_TIMEOUT_MS;
    }

    private set(patch: Partial<HomepagePhotoSnapshot>): void {
        this.snap = { ...this.snap, ...patch };
        this.listener(this.snap);
    }

    setPhotoNotice(text: string, tone: PhotoNotice['tone'] = 'error'): void {
        this.set({ photoNotice: text ? { text, tone } : null });
    }

    /** 직접 고치기(직접 올리기·빼기·대표 바꾸기) — 최신 상태에 [fn]. null 이면 그대로 두고 false */
    modify(fn: (current: ClinicPhotoState) => ClinicPhotoState | null): boolean {
        const next: ClinicPhotoState | null = fn(this.snap.state);
        if (!next) return false;
        this.set({ state: next, photoNotice: null, fieldMessage: '' });
        return true;
    }

    /** 후보 하나를 서버에 저장(이미 저장했으면 그 주소) */
    private async saveCandidate(candidate: string, waitMs: number = this.timeoutMs): Promise<string> {
        const known: string | undefined = this.snap.savedByCandidate[candidate];
        if (known) return known;
        const res = await withTimeout(this.options.importPhoto(candidate), waitMs);
        const saved: string | undefined = res.result?.url;
        if (!saved) throw new Error(res.message || '사진을 가져오지 못했어요.');
        this.set({ savedByCandidate: { ...this.snap.savedByCandidate, [candidate]: saved } });
        return saved;
    }

    /** 자동으로 넣었던 추가 사진을 뺀 상태(대표는 그대로) */
    private withoutAutoExtras(state: ClinicPhotoState): ClinicPhotoState {
        if (this.autoExtras.size === 0) return state;
        const auto: Set<string> = this.autoExtras;
        this.autoExtras = new Set();
        const rest: string[] = state.images.slice(1).filter((url) => !auto.has(url));
        if (rest.length === state.images.length - 1) return state;
        return { images: [...state.images.slice(0, 1), ...rest], mainSource: state.mainSource };
    }

    /** 실패·주소 비움 — 가입은 기본 이미지(자동 사진은 빼고), 수정은 자동으로 넣었던 대표를 저장된 원래 사진으로(자동 추가 사진도 뺀다) */
    private fallbackState(): Partial<HomepagePhotoSnapshot> {
        const current: ClinicPhotoState = this.snap.state;
        const base: ClinicPhotoState = this.withoutAutoExtras(current);
        const next: ClinicPhotoState = this.options.mode === 'signup' ? applyFallbackMain(base) : restoreOriginalMain(base, this.original);
        return next === current ? {} : { state: next, fieldMessage: '' };
    }

    /**
     * 홈페이지 읽기 — 칸에서 벗어날 때(blur)·가입 2단계 처음·[홈페이지로 자동 채우기]([force])·[홈페이지 칸에 넣기].
     * 주소가 비었거나 형식이 틀리면 읽지 않는다(가입은 기본 이미지, 자동 사진은 되돌린다).
     */
    async readHomepage(raw: string, options: { force?: boolean } = {}): Promise<void> {
        const mode: HomepagePhotoMode = this.options.mode;
        const key: string = homepageKey(raw);
        if (!key) {
            // 칸을 비우거나 틀린 주소 — 진행 중인 읽기는 버리고, 다시 같은 주소를 넣으면 읽게 한다
            this.seq += 1;
            this.lastKey = null;
            this.set({ loading: false, message: '', preview: null, ...this.fallbackState() });
            return;
        }
        if (mode === 'edit' && !options.force && key === this.initialKey) {
            // 저장된 원래 주소로 되돌림 — 읽지 않고 처음 사진 상태로(자동으로 넣었던 대표를 빼고 원래 대표를 맨 앞에)
            if (this.lastKey !== key) {
                this.seq += 1;
                this.lastKey = key;
                const restored: ClinicPhotoState = restoreOriginalMain(this.withoutAutoExtras(this.snap.state), this.original);
                this.set({ loading: false, message: '', fieldMessage: '', preview: null, state: restored });
            }
            return;
        }
        if (!shouldReadHomepage(raw, this.lastKey, options.force)) return;
        this.lastKey = key;
        this.seq += 1;
        const seq: number = this.seq;
        const deadline: number = Date.now() + this.timeoutMs;
        // 늦게 온 결과 — 버리고, 같은 읽기 차례인데 주소만 바뀐 경우면 그 주소를 다시 넣었을 때 또 읽게 한다
        const stale = (): boolean => {
            if (!isStaleHomepageResult(key, this.options.currentUrl(), seq, this.seq)) return false;
            if (seq === this.seq && this.lastKey === key) this.lastKey = null;
            return true;
        };
        /** 실패 — 다음에 칸에서 벗어날 때 다시 읽게 열쇠를 지운다 */
        const fail = (text: string): void => {
            if (this.lastKey === key) this.lastKey = null;
            this.set({ preview: null, message: text, ...this.fallbackState() });
        };
        this.set({ loading: true, preview: null, message: '', fieldMessage: '', photoNotice: null });
        try {
            let found: HomepagePreview | undefined;
            try {
                const res = await withTimeout(this.options.readPreview(raw.trim()), deadline - Date.now());
                found = res.result;
                if (!found) {
                    if (!stale()) fail(res.message || '홈페이지를 읽지 못했어요.');
                    return;
                }
            } catch (error) {
                if (!stale()) fail(messageOf(error, '홈페이지를 읽지 못했어요. 잠시 뒤 다시 시도해 주세요.'));
                return;
            }
            if (stale()) return;
            if (found.failure) {
                fail(found.failure);
                return;
            }
            const candidates: string[] = found.thumbnailCandidates ?? [];
            const count: number = candidates.length;
            if (count === 0) {
                this.set({ preview: found, message: '홈페이지에서 쓸 만한 큰 사진은 찾지 못했어요.', ...this.fallbackState() });
                return;
            }
            // 새 결과 — 앞 주소에서 자동으로 넣었던 추가 사진은 뺀다
            const stripped: ClinicPhotoState = this.withoutAutoExtras(this.snap.state);
            if (stripped !== this.snap.state) this.set({ state: stripped });
            // 저장된 원래 주소를 [홈페이지로 자동 채우기]로 다시 읽을 때는 저장돼 있던 사진을 그대로 둔다(같은 홈페이지 — 사진이 겹친다)
            const sameAsSaved: boolean = mode === 'edit' && key === this.initialKey && this.snap.state.mainSource === 'saved';
            if (sameAsSaved) {
                this.set({ preview: found, message: '' });
                return;
            }
            if (!canAddPhoto(this.snap.state)) {
                // 목록 상한 — 더 넣지 않는다
                this.set({ preview: found, message: `홈페이지에서 사진 ${count}장을 찾았어요. 치과 이미지가 너무 많아 더 넣지 않았어요.` });
                return;
            }
            const before: ClinicPhotoState = this.snap.state;
            /** 첫 사진을 대표로 넣는가 — 직접 올린 대표면 지키고 모두 추가 사진으로 */
            const replaceMain: boolean = canApplyAutoMain(before);
            if (replaceMain) {
                let saved: string;
                try {
                    saved = await this.saveCandidate(candidates[0], deadline - Date.now());
                } catch {
                    if (!stale()) fail(`홈페이지에서 사진 ${count}장을 찾았지만 첫 사진을 가져오지 못했어요.`);
                    return;
                }
                if (stale()) return;
                const next: ClinicPhotoState | null = applyAutoMain(this.snap.state, saved);
                if (next) this.set({ preview: found, state: next });
            }
            // 나머지 후보 — 치과 이미지 뒤에 바로(5장을 넘어도 목록 상한까지). 동시에 가져오고 홈페이지 순서대로 넣는다, 한 장 실패는 건너뛴다
            const room: number = MAX_LIST_IMAGES - realPhotoCount(this.snap.state.images);
            const rest: string[] = candidates.slice(replaceMain ? 1 : 0).slice(0, Math.max(0, room));
            const results = await Promise.allSettled(rest.map((candidate) => this.saveCandidate(candidate, deadline - Date.now())));
            if (stale()) return;
            let added: number = 0;
            let next: ClinicPhotoState = this.snap.state;
            for (const result of results) {
                if (result.status !== 'fulfilled') continue;
                const put: ClinicPhotoState | null = addPhoto(next, result.value);
                if (!put) continue;
                this.autoExtras.add(result.value);
                next = put;
                added += 1;
            }
            if (added > 0) this.set({ state: next });
            // 넣은 결과 안내 문구는 두지 않는다 — 사진 칸이 바로 아래라 바뀐 게 보이고, 5장 넘음은 사진 칸 빨간 한도로(2026-10-30)
            this.set({ preview: found, message: '', fieldMessage: '' });
        } finally {
            if (seq === this.seq && this.snap.loading) this.set({ loading: false });
        }
    }
}
